import React, { useEffect, useMemo, useRef, useState } from 'react';
import { getApiBaseUrl, readJsonResponse } from '../apiBase';
import {
    AS_REPAIR_STATUS_OPTIONS,
    AS_REPAIR_WORKFLOW_PHASE,
    applyAsRepairAutoValuesToForm,
    buildAsRepairListSummary,
    buildEmptyAsRepairFormValues,
    formatAsRepairFieldDisplayText,
    normalizeAsRepairFormConfig,
    splitAsRepairPayload,
    validateAsRepairFormValues,
} from '../utils/asRepairFormConfig';
import AsRepairAutoCalculatedField from './AsRepairAutoCalculatedField';
import AsRepairQuantityStepper from './AsRepairQuantityStepper';
import SpecDateInput from './SpecDateInput';

const JSON_HEADERS = { 'Content-Type': 'application/json' };

const valuesToPreviewRow = (config, values) => {
    const withAuto = applyAsRepairAutoValuesToForm(config.fields, values);
    const { known, extra } = splitAsRepairPayload(config, withAuto);
    return { ...known, extra };
};

const fieldGridClass = (field) => {
    if (field.inputType === 'textarea') {
        return 'as-repair-add-field as-repair-add-field--span-row';
    }
    return 'as-repair-add-field';
};

const AsRepairAddPage = ({
    canManageAsRepair = false,
    currentUserName = '',
    asRepairFormConfig,
    showAppToast,
    onNavigateBack,
}) => {
    const formConfig = useMemo(
        () => normalizeAsRepairFormConfig(asRepairFormConfig),
        [asRepairFormConfig],
    );
    const fields = formConfig.fields;
    const listSummary = useMemo(
        () => buildAsRepairListSummary(formConfig),
        [formConfig],
    );

    const firstInputRef = useRef(null);
    const pendingIdRef = useRef(0);
    const [form, setForm] = useState(() => buildEmptyAsRepairFormValues(formConfig));
    const [pendingEntries, setPendingEntries] = useState([]);
    const [expandedPendingIds, setExpandedPendingIds] = useState(() => new Set());
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        setForm(buildEmptyAsRepairFormValues(formConfig));
        setPendingEntries([]);
        setExpandedPendingIds(new Set());
        pendingIdRef.current = 0;
        const t = window.setTimeout(() => firstInputRef.current?.focus(), 50);
        return () => window.clearTimeout(t);
    }, [formConfig]);

    const applyFormChange = (key, raw) => {
        setForm((prev) => {
            const next = { ...prev, [key]: raw };
            return applyAsRepairAutoValuesToForm(fields, next);
        });
    };

    const renderEditableField = (field, values, onChange, { idPrefix, inputRef }) => {
        const value = values[field.key];
        const id = `${idPrefix}-${field.key}`;
        const required = !!field.required;

        if (field.inputType === 'quantity') {
            return (
                <AsRepairQuantityStepper
                    key={field.key}
                    id={id}
                    label={field.label}
                    value={value == null ? 0 : value}
                    required={required}
                    inputRef={inputRef}
                    onChange={(next) => onChange(field.key, next)}
                    disabled={busy}
                />
            );
        }

        if (field.inputType === 'auto') {
            return (
                <AsRepairAutoCalculatedField
                    key={field.key}
                    id={id}
                    label={field.label}
                    required={required}
                    field={field}
                    formConfig={formConfig}
                    values={values}
                    inputRef={inputRef}
                />
            );
        }

        if (field.inputType === 'status') {
            return (
                <div key={field.key} className={fieldGridClass(field)}>
                    <label className="field-label" htmlFor={id}>
                        {field.label}
                        {required ? <span className="as-repair-required-mark"> *</span> : null}
                    </label>
                    <select
                        id={id}
                        className="field-input"
                        value={value || 'received'}
                        onChange={(e) => onChange(field.key, e.target.value)}
                        onClick={(e) => e.stopPropagation()}
                        disabled={busy}
                    >
                        {AS_REPAIR_STATUS_OPTIONS.map((opt) => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                    </select>
                </div>
            );
        }

        if (field.inputType === 'date') {
            return (
                <div key={field.key} className={fieldGridClass(field)}>
                    <label className="field-label" htmlFor={id}>
                        {field.label}
                        {required ? <span className="as-repair-required-mark"> *</span> : null}
                    </label>
                    <SpecDateInput
                        value={value}
                        onChange={(next) => onChange(field.key, next)}
                        disabled={busy}
                        className="field-input spec-date-input"
                    />
                </div>
            );
        }

        if (field.inputType === 'textarea') {
            return (
                <div key={field.key} className={fieldGridClass(field)}>
                    <label className="field-label" htmlFor={id}>
                        {field.label}
                        {required ? <span className="as-repair-required-mark"> *</span> : null}
                    </label>
                    <textarea
                        id={id}
                        className="field-input as-repair-add-textarea"
                        value={value ?? ''}
                        onChange={(e) => onChange(field.key, e.target.value)}
                        onClick={(e) => e.stopPropagation()}
                        disabled={busy}
                        rows={3}
                    />
                </div>
            );
        }

        return (
            <div key={field.key} className={fieldGridClass(field)}>
                <label className="field-label" htmlFor={id}>
                    {field.label}
                    {required ? <span className="as-repair-required-mark"> *</span> : null}
                </label>
                <input
                    id={id}
                    ref={inputRef}
                    type="text"
                    className="field-input"
                    value={value ?? ''}
                    onChange={(e) => onChange(field.key, e.target.value)}
                    onClick={(e) => e.stopPropagation()}
                    disabled={busy}
                />
            </div>
        );
    };

    const handleAddToPending = () => {
        const withAuto = applyAsRepairAutoValuesToForm(fields, form);
        const validationMsg = validateAsRepairFormValues(formConfig, withAuto);
        if (validationMsg) {
            showAppToast?.(validationMsg, 'error');
            return;
        }
        pendingIdRef.current += 1;
        const entryId = `pending-${pendingIdRef.current}`;
        setPendingEntries((prev) => [...prev, { id: entryId, values: withAuto }]);
        setForm(buildEmptyAsRepairFormValues(formConfig));
        showAppToast?.('추가할 목록에 담았습니다.', 'success');
    };

    const handleRemovePending = (entryId, e) => {
        e.stopPropagation();
        setPendingEntries((prev) => prev.filter((entry) => entry.id !== entryId));
        setExpandedPendingIds((prev) => {
            const next = new Set(prev);
            next.delete(entryId);
            return next;
        });
    };

    const togglePendingExpanded = (entryId) => {
        setExpandedPendingIds((prev) => {
            const next = new Set(prev);
            if (next.has(entryId)) {
                next.delete(entryId);
            } else {
                next.add(entryId);
            }
            return next;
        });
    };

    const handlePendingRowKeyDown = (e, entryId) => {
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            togglePendingExpanded(entryId);
        }
    };

    const updatePendingValues = (entryId, key, raw) => {
        setPendingEntries((prev) => prev.map((entry) => {
            if (entry.id !== entryId) {
                return entry;
            }
            const nextValues = applyAsRepairAutoValuesToForm(fields, {
                ...entry.values,
                [key]: raw,
            });
            return { ...entry, values: nextValues };
        }));
    };

    const postOneCase = async (values) => {
        const withAuto = applyAsRepairAutoValuesToForm(fields, values);
        const res = await fetch(`${getApiBaseUrl()}/as-repair-cases`, {
            method: 'POST',
            headers: JSON_HEADERS,
            body: JSON.stringify({
                requesterName: currentUserName,
                _asRepairPhase: AS_REPAIR_WORKFLOW_PHASE.QTY_CHECK,
                ...withAuto,
            }),
        });
        const data = await readJsonResponse(res);
        if (!data || data.id == null) {
            throw new Error('서버 응답이 올바르지 않습니다.');
        }
    };

    const handleSubmitAll = async () => {
        if (pendingEntries.length === 0) {
            showAppToast?.('추가할 목록이 비어 있습니다. 「리스트 추가」로 항목을 담아주세요.', 'error');
            return;
        }
        setBusy(true);
        let successCount = 0;
        const failed = [];
        try {
            for (let i = 0; i < pendingEntries.length; i += 1) {
                const entry = pendingEntries[i];
                try {
                    await postOneCase(entry.values);
                    successCount += 1;
                } catch (err) {
                    failed.push({ index: i + 1, message: err.message || '등록 실패' });
                }
            }
            if (successCount > 0 && failed.length === 0) {
                showAppToast?.(`A/S 목록 ${successCount}건을 등록했습니다.`, 'success');
                onNavigateBack?.();
                return;
            }
            if (successCount > 0) {
                showAppToast?.(
                    `${successCount}건 등록, ${failed.length}건 실패했습니다.`,
                    'error',
                );
                setPendingEntries((prev) => prev.filter((_, idx) => failed.some((f) => f.index === idx + 1)));
            } else {
                showAppToast?.(failed[0]?.message || '등록에 실패했습니다.', 'error');
            }
        } finally {
            setBusy(false);
        }
    };

    const renderPendingDetail = (entry) => {
        const onChange = (key, raw) => updatePendingValues(entry.id, key, raw);
        return (
            <div
                className="as-repair-add-fields-grid"
                onClick={(e) => e.stopPropagation()}
                onKeyDown={(e) => e.stopPropagation()}
                role="presentation"
            >
                {fields.map((field) => renderEditableField(
                    field,
                    entry.values,
                    onChange,
                    { idPrefix: `pending-${entry.id}`, inputRef: null },
                ))}
            </div>
        );
    };

    if (!canManageAsRepair) {
        return (
            <div className="as-repair-add-page">
                <p className="as-repair-manage-help">A/S 수리 관리 권한이 없습니다.</p>
            </div>
        );
    }

    return (
        <div className="as-repair-add-page">
            <section className="model-list-section model-list-section--asRepair">
                <div className="as-repair-add-page-head">
                    <h3>A/S 목록 추가</h3>
                    <button
                        type="button"
                        className="button button-cancel"
                        onClick={() => onNavigateBack?.()}
                        disabled={busy}
                    >
                        목록으로
                    </button>
                </div>

                <div className="as-repair-add-panel as-repair-add-modal">
                    <div className="as-repair-add-form">
                        <div className="as-repair-add-fields-grid">
                            {fields.map((field, index) => renderEditableField(
                                field,
                                form,
                                applyFormChange,
                                {
                                    idPrefix: 'as-repair-add',
                                    inputRef: index === 0 ? firstInputRef : null,
                                },
                            ))}
                        </div>
                        <div className="as-repair-add-modal-actions">
                            <button
                                type="button"
                                className="button as-repair-add-list-btn"
                                onClick={handleAddToPending}
                                disabled={busy}
                            >
                                리스트 추가
                            </button>
                            <button
                                type="button"
                                className="button button-cancel"
                                onClick={() => onNavigateBack?.()}
                                disabled={busy}
                            >
                                취소
                            </button>
                            <button
                                type="button"
                                className="button"
                                onClick={handleSubmitAll}
                                disabled={busy || pendingEntries.length === 0}
                            >
                                {busy ? '등록중…' : `추가 (${pendingEntries.length})`}
                            </button>
                        </div>
                    </div>
                </div>

                <section className="as-repair-add-pending-section">
                    <h4 className="as-repair-add-pending-title">
                        추가할 목록
                        {pendingEntries.length > 0 ? (
                            <span className="as-repair-add-pending-count">{pendingEntries.length}건</span>
                        ) : null}
                    </h4>
                    {pendingEntries.length === 0 ? (
                        <p className="empty-list-message as-repair-add-pending-empty">
                            위에서 입력 후 「리스트 추가」를 누르면 여기에 쌓입니다.
                        </p>
                    ) : (
                        <div className="as-repair-add-pending-list">
                            {pendingEntries.map((entry, index) => {
                                const expanded = expandedPendingIds.has(entry.id);
                                const previewRow = valuesToPreviewRow(formConfig, entry.values);
                                return (
                                    <div key={entry.id} id={`as-repair-add-pending-${entry.id}`}>
                                        <div
                                            className={`model-name model-name-general as-repair-list-row as-repair-add-pending-row${expanded ? ' as-repair-list-row--open' : ''}`}
                                            onClick={() => togglePendingExpanded(entry.id)}
                                            onKeyDown={(e) => handlePendingRowKeyDown(e, entry.id)}
                                            role="button"
                                            tabIndex={0}
                                            aria-expanded={expanded}
                                        >
                                            <span className="as-repair-add-pending-index">{index + 1}</span>
                                            <div className="as-repair-list-main-cols as-repair-add-pending-summary-cols">
                                                {listSummary.columns.map((col) => {
                                                    const text = formatAsRepairFieldDisplayText(
                                                        col.field,
                                                        previewRow,
                                                        formConfig,
                                                    );
                                                    return (
                                                        <div
                                                            key={col.id}
                                                            className="as-repair-summary-item"
                                                            title={`${col.displayLabel}: ${text}`}
                                                        >
                                                            <span className="as-repair-summary-label">{col.displayLabel}:</span>
                                                            <span className="as-repair-summary-value">{text}</span>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                            <button
                                                type="button"
                                                className="button button-cancel as-repair-row-delete-btn"
                                                disabled={busy}
                                                onClick={(e) => handleRemovePending(entry.id, e)}
                                            >
                                                삭제
                                            </button>
                                        </div>
                                        {expanded ? (
                                            <div
                                                className="as-repair-expanded-detail"
                                                role="presentation"
                                            >
                                                {renderPendingDetail(entry)}
                                            </div>
                                        ) : null}
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </section>
            </section>
        </div>
    );
};

export default AsRepairAddPage;
